import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../../lib/api'
import { Section } from '../../components/ui'
import type { ActionCenterAlertItem, ActionCenterResponse, AdminNote, Priority } from '../../lib/types'

const PRIORITY_COLORS: Record<Priority, string> = {
  critical: 'badge-red',
  high: 'badge-orange',
  medium: 'badge-amber',
  low: 'badge-grey',
}

function linkFor(item: { link_type: string; link_id: string }): string {
  if (item.link_type === 'organization') return `/admin/organizations/${item.link_id}`
  if (item.link_type === 'provider') return `/admin/providers/${item.link_id}`
  if (item.link_type === 'patient') return `/admin/patients/${item.link_id}`
  return '/admin/overview'
}

function entityLinkFor(n: AdminNote): string {
  if (n.attached_to_type === 'organization') return `/admin/organizations/${n.attached_to_id}`
  if (n.attached_to_type === 'provider') return `/admin/providers/${n.attached_to_id}`
  if (n.attached_to_type === 'patient') return `/admin/patients/${n.attached_to_id}`
  return '/admin/overview'
}

function AlertRow({ item }: { item: ActionCenterAlertItem }) {
  return (
    <Link to={linkFor(item)} className="row" style={{ textDecoration: 'none', color: 'inherit' }}>
      <span>
        <span className={`badge ${PRIORITY_COLORS[item.severity]}`} style={{ marginRight: 8 }}>{item.severity}</span>
        {item.summary}
      </span>
    </Link>
  )
}

function TaskRow({ task }: { task: AdminNote }) {
  return (
    <Link to={entityLinkFor(task)} className="row" style={{ textDecoration: 'none', color: 'inherit' }}>
      <span>
        <span className={`badge ${PRIORITY_COLORS[task.priority]}`} style={{ marginRight: 8 }}>{task.priority}</span>
        <span className="badge badge-grey" style={{ marginRight: 8 }}>{task.category}</span>
        {task.content}
      </span>
      <span className="muted" style={{ fontSize: 12 }}>
        {task.due_at ? new Date(task.due_at).toLocaleDateString() : ''}
      </span>
    </Link>
  )
}

export default function AdminActionCenter() {
  const [data, setData] = useState<ActionCenterResponse | null>(null)
  const [includeDemo, setIncludeDemo] = useState(false)

  useEffect(() => {
    api(`/admin/action-center${includeDemo ? '?include_demo=true' : ''}`).then(setData)
  }, [includeDemo])

  if (!data) return <div className="page-loading">Loading action center…</div>

  const { my_work, alerts } = data
  const totalAlerts = alerts.reduce((sum, c) => sum + c.count, 0)

  return (
    <div>
      <div className="page-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1>Action Center</h1>
          <div className="ph-sub">What needs attention today — your worklist, then platform-wide alerts, most severe first.</div>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <input type="checkbox" checked={includeDemo} onChange={(e) => setIncludeDemo(e.target.checked)} />
          Include demo network
        </label>
      </div>

      <Section title="My Work" note={`${my_work.assigned_to_me.length} open`}>
        {my_work.overdue.length > 0 && (
          <>
            <h3 style={{ fontSize: 13, color: 'var(--red)', margin: '10px 0 4px' }}>Overdue</h3>
            <div className="row-list">
              {my_work.overdue.map((t) => <TaskRow key={t.id} task={t} />)}
            </div>
          </>
        )}
        {my_work.due_today.length > 0 && (
          <>
            <h3 style={{ fontSize: 13, color: 'var(--amber)', margin: '10px 0 4px' }}>Due today</h3>
            <div className="row-list">
              {my_work.due_today.map((t) => <TaskRow key={t.id} task={t} />)}
            </div>
          </>
        )}
        <h3 style={{ fontSize: 13, color: 'var(--muted)', margin: '10px 0 4px' }}>All assigned to me</h3>
        <div className="row-list">
          {my_work.assigned_to_me.map((t) => <TaskRow key={t.id} task={t} />)}
          {my_work.assigned_to_me.length === 0 && <div className="empty">Nothing assigned to you right now.</div>}
        </div>
        {my_work.recently_completed.length > 0 && (
          <>
            <h3 style={{ fontSize: 13, color: 'var(--muted)', margin: '10px 0 4px' }}>Recently completed</h3>
            <div className="row-list">
              {my_work.recently_completed.map((t) => <TaskRow key={t.id} task={t} />)}
            </div>
          </>
        )}
      </Section>

      <Section title="Platform alerts" note={`${totalAlerts} total`}>
        {alerts.filter((cat) => cat.count > 0).map((cat) => (
          <div key={cat.key} className="section-block">
            <div className="section-head"><h2 style={{ fontSize: 15 }}>{cat.label}</h2><span className="sh-note">{cat.count}</span></div>
            <div className="row-list">
              {cat.items.map((item) => <AlertRow key={item.id} item={item} />)}
            </div>
          </div>
        ))}
        {totalAlerts === 0 && <div className="empty">Nothing needs attention right now.</div>}
      </Section>
    </div>
  )
}
